import { CollectionViewer, DataSource } from '@angular/cdk/collections'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { FormGroup } from '@angular/forms'
import { delay, filter, finalize, first, map, mapTo, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import dayjs, { Dayjs, isDayjs } from 'dayjs'

import { BehaviorSubject, merge, Observable, of, pipe, Subject, UnaryFunction } from 'rxjs'
import { FilterValues } from '@shared/models/filter-values.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'

type DataResponse = {
  meta: { limit: number; page: number; total: number }
  additionalFields: { total: number }
  data: unknown
}

export abstract class AbstractApiDataSource<T> extends DataSource<T> {
  private sortValue: string
  private filterValues: { [param: string]: string | number | (string | number)[] }

  protected readonly autoload: boolean = true
  protected readonly refresh$ = new Subject<void>()

  protected isLoading = new BehaviorSubject<boolean>(false)
  readonly isLoading$ = this.isLoading.asObservable()

  protected data = new BehaviorSubject<unknown>(null)
  readonly data$ = this.data.asObservable()

  protected totalValues = new BehaviorSubject<{ [param: string]: number | string }>(null)
  readonly totalValues$ = this.totalValues.asObservable().pipe(filter(Boolean))

  protected isEmptyData = new BehaviorSubject<boolean>(false)
  readonly isEmptyData$ = this.isEmptyData.asObservable()

  constructor(
    protected sort: MatSort,
    protected paginator: MatPaginator,
    protected form: FormGroup,
  ) {
    super()
  }

  connect(): Observable<T[]> {
    const instantMutations = [this.refresh$, this.sort.sortChange, this.paginator.page]

    const delayedMutations = [this.form.valueChanges]

    const allMutations = [...instantMutations, ...delayedMutations]

    const takeUntilNewEmission = () => takeUntil(merge(...allMutations))
    const switchToDebounced = () => switchMap((value) => of(value).pipe(delay(1000), takeUntilNewEmission()))

    const resultedDataMutations = [
      ...instantMutations,
      this.form.valueChanges.pipe(
        switchToDebounced(),
        tap(() => (this.paginator.pageIndex = 0)),
      ),
    ]

    return merge(...resultedDataMutations).pipe(
      this.startWith(),
      switchMap(() => this.getData()),
    )
  }

  startWith(): UnaryFunction<Observable<unknown>, Observable<unknown>> {
    return this.autoload ? pipe(startWith(true), delay(0)) : mapTo((value: unknown) => value)
  }

  private getData(): Observable<T[]> {
    this.filterValues = this.getFilterSameOldValues()
    this.sortValue = this.getSortValue()
    const skipApiCall: boolean = (() => {
      const value = sessionStorage.getItem('skipApiCall')
      return value === null || value === 'undefined' ? false : (JSON.parse(value) as boolean)
    })()

    return of(null).pipe(
      filter(() => !skipApiCall),
      tap(() => this.isLoading.next(true)),
      switchMap(() => this.getApiData(this.sortValue, this.filterValues)),
      tap((response: DataResponse) => this.handleResponseMeta(response)),
      tap((response: DataResponse) => this.setTotalValues(response)),
      tap((response: DataResponse) => this.setData(response)),
      map((response: DataResponse) => response.data as T[]),
      tap((data: T[]) => this.setIsEmptyData(!data.length)),
      finalize(() => this.isLoading.next(false)),
    )
  }

  getSortValue() {
    const sortActive = this.sort.active ? this.sort.active : null
    const sortPrefix = this.sort.direction === 'desc' ? '-' : ''
    const sortValue = sortActive ? `${sortPrefix}${sortActive}` : ''

    return sortValue
  }

  getFilterValues() {
    const filterValues = {}

    for (const key in this.form.value) {
      const formValue = this.form.value as Record<string, string>
      if (![null, ''].includes(formValue[key])) {
        if (this.isNumericColumn(key)) {
          formValue[key].split('&').forEach((keyValue) => {
            const [field, value] = keyValue.split('=')
            filterValues[field] = value
          })
        } else if (this.isRangeDatepickerColumn(key)) {
          const formValueDate = this.form.value as Record<string, { startDate: Dayjs; endDate: Dayjs }>
          const { startDate, endDate } = formValueDate[key]

          const start = dayjs(startDate).isValid() ? dayjs(startDate).tz().format() : startDate
          const end = dayjs(endDate).isValid() ? dayjs(endDate).tz().format() && dayjs(endDate).tz().clone().format() : endDate

          if (start && end) {
            filterValues[`${key}[gte]`] = start
            filterValues[`${key}[lt]`] = end
          } else {
            delete filterValues[`${key}[gte]`]
            delete filterValues[`${key}[lt]`]
          }
        } else if (isDayjs((this.form.value as Record<string, Dayjs>)[key]) || ['startDate', 'endDate'].includes(key)) {
          filterValues[key] = dayjs((this.form.value as Record<string, Dayjs>)[key])
            .tz()
            .format()
        } else {
          filterValues[key] = formValue[key]
        }
      }
    }
    return filterValues
  }

  //Removed the tz() method calls to avoid applying any timezone offset
  getFilterSameOldValues() {
    const filterValues = {}

    for (const key in this.form.value) {
      const formValue = this.form.value as Record<string, string>
      if (![null, ''].includes(formValue[key])) {
        if (this.isNumericColumn(key)) {
          formValue[key].split('&').forEach((keyValue) => {
            const [field, value] = keyValue.split('=')
            filterValues[field] = value
          })
        } else if (this.isRangeDatepickerColumn(key)) {
          const formValueDate = this.form.value as Record<string, { startDate: Dayjs; endDate: Dayjs }>
          const { startDate, endDate } = formValueDate[key]

          const start = dayjs(startDate).isValid() ? dayjs(startDate).format() : startDate
          const end = dayjs(endDate).isValid() ? dayjs(endDate).format() : endDate

          if (start && end) {
            filterValues[`${key}[gte]`] = start
            filterValues[`${key}[lt]`] = end
          } else {
            delete filterValues[`${key}[gte]`]
            delete filterValues[`${key}[lt]`]
          }
        } else if (isDayjs((this.form.value as Record<string, Dayjs>)[key]) || ['startDate', 'endDate'].includes(key)) {
          filterValues[key] = dayjs((this.form.value as Record<string, Dayjs>)[key]).format()
        } else {
          filterValues[key] = formValue[key]
        }
      }
    }

    return filterValues
  }

  getFileData(columns: string[], type: ExportFileTypes) {
    this.filterValues = this.getFilterValues()
    this.sortValue = this.getSortValue()

    return this.getApiFileData(columns, this.sortValue, this.filterValues, type)
  }

  getScheduledReport(): void {
    this.filterValues = this.getFilterValues()
    this.sortValue = this.getSortValue()

    this.scheduledReport(this.sortValue, this.filterValues)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getApiFileData(columns: string[], sortValue: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return of<string>(null)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected scheduledReport(sortValue: string, filterValues: FilterValues): void {
    // method for generate scheduled report
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract getApiData(sortValue: string, filterValues: FilterValues): Observable<any>

  handleResponseMeta({ meta }: DataResponse): void {
    this.paginator.pageSize = meta.limit
    this.paginator.pageIndex = --meta.page
    this.paginator.length = meta.total
  }

  setTotalValues({ additionalFields }: DataResponse): void {
    if (!additionalFields) return

    const totalValues = additionalFields.total || {}
    this.totalValues.next(totalValues)
  }

  setIsEmptyData(isEmptyData: boolean): void {
    this.isEmptyData.next(isEmptyData)
  }

  setData({ data }: DataResponse): void {
    if (!data) return

    this.data.next(data)
  }

  refresh() {
    return of(null).pipe(
      tap(() => this.refresh$.next()),
      switchMap(() => this.isLoading$),
      filter((isLoading) => !isLoading),
      first(),
    )
  }

  // dirty hack, no other way, needs form rework
  private isNumericColumn(key: string): boolean {
    const value = String((this.form.value as Record<string, unknown>)[key])

    const numericValuePattern = `^${key}\\[gte\\]=[0-9]+(&${key}\\[lt\\]=[0-9]+)?$`
    return !!value.match(numericValuePattern)
  }

  private isRangeDatepickerColumn(key: string): boolean {
    const value = (this.form.value as Record<string, unknown>)[key] as Record<string, unknown>

    const isObject = typeof value === 'object' && !!value
    if (!isObject) {
      return false
    }

    const isRange = Object.keys(value).length === 3
    const isDate = 'startDate' in value && 'endDate' in value

    return isRange && isDate
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disconnect(collectionViewer: CollectionViewer): void {
    this.isLoading.complete()
    this.totalValues.complete()
  }
}

export abstract class AbstractReportDataSource<T> extends AbstractApiDataSource<T> {
  protected readonly autoload: boolean = false
}
